import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { config } from '../../config';

interface PaymentFormProps {
  enrollmentId: string;
  invoiceData: {
    id: string;
    invoice_number: string;
    total: number;
    due_date: string;
  };
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  enrollmentId,
  invoiceData,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [stripeMode, setStripeMode] = useState<string>('test');

  useEffect(() => {
    // Create payment intent when component mounts
    createPaymentIntent();
  }, []);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/camps/enrollments/${enrollmentId}/create-payment-intent`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.client_secret);
        setStripeMode(data.stripe_mode || 'test');
        console.log(`💳 Payment initialized in ${data.stripe_mode?.toUpperCase() || 'TEST'} mode`);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to initialize payment');
      }
    } catch (err: any) {
      console.error('Payment intent error:', err);
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError('Card element not found');
        setProcessing(false);
        return;
      }

      // Validate postal code
      const postalCodeRegex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
      if (!postalCodeRegex.test(postalCode)) {
        setError('Please enter a valid Canadian postal code (e.g., K1A 0A6)');
        setProcessing(false);
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              address: {
                country: 'CA',
                postal_code: postalCode
              }
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded in Stripe, now record it in our backend
        try {
          const token = localStorage.getItem('cognitoToken') || '';
          const response = await fetch(
            `${config.BACKEND_URL}/api/camps/enrollments/${enrollmentId}/confirm-payment`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                payment_intent_id: paymentIntent.id
              })
            }
          );

          if (response.ok) {
            console.log('✅ Payment recorded in backend');
            onSuccess();
          } else {
            const errorData = await response.json();
            setError(`Payment succeeded but failed to record: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (backendError: any) {
          console.error('Backend payment recording error:', backendError);
          setError('Payment succeeded but failed to record. Please contact support.');
        } finally {
          setProcessing(false);
        }
      } else {
        setProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '40px', marginBottom: '20px' }}>💳</div>
        <p>Setting up payment...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '30px' }}>
      <h3 style={{ marginBottom: '20px', fontSize: '22px' }}>Complete Payment</h3>
      
      <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
          <div>
            <strong>Invoice:</strong> {invoiceData.invoice_number}
          </div>
          <div>
            <strong>Due Date:</strong> {new Date(invoiceData.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#667eea' }}>
            Amount Due: ${invoiceData.total.toFixed(2)}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
            Card Details
          </label>
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            backgroundColor: '#fff'
          }}>
            <CardElement options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': { color: '#aab7c4' }
                },
                invalid: { color: '#9e2146' }
              },
              hidePostalCode: true
            }} />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
            Postal Code (Canadian Format) *
          </label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '');
              setPostalCode(value);
            }}
            placeholder="K1A 0A6"
            pattern="[A-Z][0-9][A-Z] [0-9][A-Z][0-9]"
            required
            style={{
              width: '100%',
              padding: '15px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              textTransform: 'uppercase'
            }}
          />
          <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
            Format: A1A 1A1 (e.g., K1A 0A6)
          </small>
        </div>

        {error && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '8px', 
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            ❌ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={processing}
            style={{ padding: '12px 24px', fontSize: '14px' }}
          >
            Pay Later
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!stripe || processing}
            style={{ padding: '12px 30px', fontSize: '14px', fontWeight: 'bold' }}
          >
            {processing ? 'Processing...' : `Pay $${invoiceData.total.toFixed(2)}`}
          </button>
        </div>
      </form>

      <div style={{ 
        marginTop: '25px', 
        paddingTop: '20px', 
        borderTop: '1px solid #dee2e6',
        textAlign: 'center',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '10px' }}>
          <span style={{ marginRight: '15px' }}>🔒 SSL Secured</span>
          <span style={{ marginRight: '15px' }}>🛡️ PCI Compliant</span>
          <span>✅ Stripe Protected</span>
        </div>
        <p style={{ margin: 0 }}>
          Your payment information is processed securely by Stripe. We never store your card details.
        </p>
      </div>
    </div>
  );
};

interface CampPaymentModalProps {
  enrollmentId: string;
  invoiceData: {
    id: string;
    invoice_number: string;
    total: number;
    due_date: string;
  };
  onSuccess: () => void;
  onCancel: () => void;
}

const CampPaymentModal: React.FC<CampPaymentModalProps> = (props) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeMode, setStripeMode] = useState<string>('test');

  useEffect(() => {
    // Fetch publishable key from API based on camp's stripe mode
    const initializeStripe = async () => {
      try {
        const token = localStorage.getItem('cognitoToken') || '';
        const response = await fetch(
          `${config.BACKEND_URL}/api/camps/enrollments/${props.enrollmentId}/create-payment-intent`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const publishableKey = data.publishable_key || config.STRIPE_PUBLISHABLE_KEY;
          const mode = data.stripe_mode || 'test';
          
          console.log(`💳 Initializing Stripe with ${mode.toUpperCase()} mode key:`, publishableKey.substring(0, 20) + '...');
          setStripeMode(mode);
          setStripePromise(loadStripe(publishableKey));
        } else {
          // Fallback to test mode
          console.log('⚠️ Failed to get publishable key, using test mode');
          setStripePromise(loadStripe(config.STRIPE_PUBLISHABLE_KEY));
        }
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        // Fallback to test mode
        setStripePromise(loadStripe(config.STRIPE_PUBLISHABLE_KEY));
      }
    };

    initializeStripe();
  }, [props.enrollmentId]);

  const elementsOptions = {
    locale: 'en' as const,
    appearance: {
      theme: 'stripe' as const
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px' }}>
            💳 Camp Enrollment Payment
            {stripeMode === 'live' && (
              <span style={{ 
                marginLeft: '10px', 
                fontSize: '12px', 
                backgroundColor: '#dc3545', 
                padding: '4px 8px', 
                borderRadius: '4px' 
              }}>
              </span>
            )}
          </h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
            Secure payment via Stripe {stripeMode === 'live' ? ' (Live Mode)' : ''}
          </p>
        </div>

        {stripePromise ? (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm {...props} />
          </Elements>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading payment system...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampPaymentModal;

